import { SelectProps } from "antd";
import { BusinessServiceDescriptor } from "src/businessServices";
import {
  ObjectServiceExt,
  ZObjectService,
} from "src/businessServices/businessServises.types";
import { onError } from "src/common/onError";
import { GroupType } from "src/types/ZGroup";
import { ZIdName } from "src/types/ZIdName";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { CommonNodeO2, ObjectO2, ValueO2 } from "../Obj2Tab/Obj2Nodes";
import { ObjectsModules } from "./modules";
import { AllObjectsPorts } from "./ports";
import { projectSingleNode } from "./view/projectNode";
import { stateOptionsOf } from "./view/stateOptions";

export type LegacyPorts = AllObjectsPorts;

/**
 * Совместимость с формами вкладки.
 *
 * Формы не переписываются, поэтому кто-то должен предъявить им ровно те члены,
 * которых они ждут. Этот адаптер и есть та самая граница: за ней домен не знает
 * ни про `ObjectO2`, ни про то, как формы устроены.
 *
 * Здесь только переадресация. Появление тут вычислений означает, что модуль
 * спроектирован неверно.
 */
export const createLegacyPorts = (m: ObjectsModules): LegacyPorts => ({
  // ---- справочники

  get attrTypesList() {
    return m.references.attrTypes;
  },
  get attrTypesDict() {
    return m.references.attrTypesDict;
  },
  get attrTypeActions() {
    return m.references.attrTypeActions;
  },
  get objectTypeActions() {
    return m.references.objectTypeActions;
  },
  get typeActions() {
    return m.references.typeActions;
  },
  get roleGroups() {
    return m.references.roleGroups;
  },
  get selectDictionaryStore() {
    return m.selectDictionaryStore;
  },
  isDictionary1Type: (typeId: number) => m.references.isDictionary1Type(typeId),
  loadObjectOptions: async (): Promise<ZIdName[]> => m.catalog.options,

  // ---- дерево

  get treeData(): CommonNodeO2[] {
    return m.treeProjection.roots;
  },

  // ---- текущий узел

  get curNode(): CommonNodeO2 | null {
    const id = m.selection.selectedId;
    return id ? projectSingleNode(id, m.projection) : null;
  },
  get currentObjectId(): ZUid | null {
    const id = m.selection.selectedId;
    return (id ? m.tree.ownerObject(id)?.data.id : null) ?? null;
  },
  get curObjectName(): string | null {
    const id = m.selection.selectedId;
    return (id ? m.tree.ownerObject(id)?.data.name : null) ?? null;
  },
  get stateOptions(): SelectProps["options"] | null {
    const id = m.selection.selectedId;
    return stateOptionsOf(m.formData.state, id ? m.tree.node(id) : undefined);
  },
  get formStore() {
    return m.formStore;
  },

  // ---- действия над деревом

  safeAddAttr: () =>
    m.formStore.safeAction(() => {
      m.drafts.addDraftAttribute().catch(onError);
    }),
  safeAddGroup: (type: GroupType) =>
    m.formStore.safeAction(() => {
      m.drafts.addDraftGroup(m.references.newGroup(type)).catch(onError);
    }),
  safeDeleteCurNode: () => m.mutations.setDeleting(m.selection.selectedId),
  safeUpToObject: () => {
    const id = m.selection.selectedId;
    const owner = id ? m.tree.ownerObject(id) : undefined;
    if (owner) m.nodeSelection.safeSelect(owner.id);
  },
  addExistingAttr: (valueNode: ValueO2, attrId: ZUid) =>
    m.drafts.attachExistingAttr(String(valueNode.key), attrId),
  onNewObject: (details: ZObjectItem) => {
    m.mutations.addObject(details).catch(onError);
  },

  // ---- порядок и настройки списка

  get objOrderStore() {
    return m.orderStore;
  },
  get objTableSettingsStore() {
    return m.tableSettingsStore;
  },
  // Ключи редактора порядка не совпадают с идентификаторами дерева, и сводить
  // их не нужно: достаточно знать, какой объект он редактировал
  updateOrder: () => {
    const objectId = m.orderStore.srcObject?.id;
    if (objectId !== undefined) {
      m.mutations.reorderApplied(objectId).catch(onError);
    }
  },

  // ---- бизнес-сервисы

  get servUpdating() {
    return m.business.servUpdating;
  },
  createService: (objNode: ObjectO2, values: unknown) =>
    m.business.createService(objNode, values),
  updateService: (
    objNode: ObjectO2,
    bs: BusinessServiceDescriptor,
    servInfo: ZObjectService,
    values: unknown,
  ) => m.business.updateService(objNode, bs, servInfo, values),
  deleteService: (objNode: ObjectO2, servInfo: ObjectServiceExt) =>
    m.business.deleteService(objNode, servInfo),
});
